import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-20 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-file-unknow-line text-2xl"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} · Galgame资源大全`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},galgame资源,galgame资源下载`}
        canonicalUrl={`https://galgame-resources.com/article/${article.slug}`}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.date,
          url: `https://galgame-resources.com/article/${article.slug}`,
          publisher: {
            '@type': 'Organization',
            name: 'Galgame资源大全',
          },
        }}
      />

      <Navbar />
      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <div className="flex items-center gap-1 text-xs text-foreground-400">
                  <i className="ri-calendar-line"></i>
                  <span>{article.date}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-foreground-400">
                  <i className="ri-time-line"></i>
                  <span>{article.readTime}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-foreground-400">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-200 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden hover:bg-background-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-bold text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}